import { Injectable } from '@angular/core';
import { ProvisionRole } from '@app/auth/models/users.model';
import { skySwitchDB } from '@app/core/db/skyswitch-db';
import { Response } from '@app/core/models/api.models';
import { AppFeature } from '@app/core/models/config.models';
import { ApiService } from '@app/core/services/api.service';
import { AppConfigService } from '@app/core/services/app-config.service';
import {
  DefaultRecordingAnnouncementSettings,
  RecordingAnnouncement,
  RecordingAnnouncementSettings,
  RecordingAnnouncementSettingsDto,
  RecordingAnnouncementsListItem,
} from '@app/preferences/models/recording-announcements.models';
import { VoiceOptions } from '@app/preferences/models/settings.models';
import { TTSLanguage } from '@app/preferences/models/voicemail.model';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, firstValueFrom, forkJoin, map, Observable, of, take, tap, timeout } from 'rxjs';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class RecordingAnnouncementService extends ApiService {
  private readonly languagesSubject = new BehaviorSubject<TTSLanguage[]>([]);
  public readonly languages$ = this.languagesSubject.asObservable();

  private readonly filteredVoicesByLanguageSubject = new BehaviorSubject<VoiceOptions[]>([]);
  public readonly filteredVoicesByLanguage$ = this.filteredVoicesByLanguageSubject.asObservable();

  private readonly announcementSettingsSubject = new BehaviorSubject<RecordingAnnouncementSettings>({
    ...DefaultRecordingAnnouncementSettings,
    ttsMessages: DefaultRecordingAnnouncementSettings.ttsMessages.map((message) =>
      this.translateService.instant(message)
    ),
  });
  public readonly announcementSettings$: Observable<RecordingAnnouncementSettings>;

  private voicesByLanguage: { [key: string]: VoiceOptions[] } = {};

  constructor(
    private appConfigService: AppConfigService,
    private translateService: TranslateService
  ) {
    super('users/{me}');

    // using forkJoin because want to execute inside code only once
    forkJoin([
      // Provision is already fetched and cached by AppInitializerService before this service is
      // constructed — loadedProvision$ replays it directly, so we use what was just fetched
      // instead of reading it back separately via appConfigService.provision.
      // It emits the Provision on success and null on a definitive failure (via
      // notifyProvisionLoadFailed()), so the fallback to default roles arrives immediately in
      // either case — timeout(30_000) only guards against an attempt that never concludes either way.
      appConfigService.loadedProvision$.pipe(
        take(1),
        timeout(30_000),
        catchError(() => of(null)),
        map((provision) => provision ?? { data: { roles: [''] } })
      ),
      this.appConfigService.features$.pipe(take(2)), // take 2nd value as first contains only default settings
    ]).subscribe(async ([provision, features]) => {
      if (features[AppFeature.CallRecordingAnnouncement]) {
        this.syncAnnouncements().then(() => {
          if (provision.data.roles.includes(ProvisionRole.OfficeManager)) {
            this.checkAnnouncements();
          }
        });
      }
    });

    this.announcementSettings$ = this.announcementSettingsSubject.pipe(
      tap({
        subscribe: () => {
          this.getPreferences();
          this.getLanguages();
        },
      })
    );
  }

  public async getRecordingAnnouncement(announcement: number | RecordingAnnouncement): Promise<Blob | undefined> {
    // announcements are indexed 1-4 in storage (indexed db and web) and 0-3 in settings component (for better manipulation)
    // in other words, string indices are 1-4, numerical indices are 0-3
    const dbAnnouncement =
      typeof announcement === 'number'
        ? await skySwitchDB.announcements.get(String(announcement + 1))
        : await skySwitchDB.announcements.get(announcement);

    return dbAnnouncement?.announcement;
  }

  public async uploadRecordingAnnouncements(
    announcements: Array<{ index: number; announcement: Blob }>
  ): Promise<void> {
    await Promise.all(
      announcements.map((announcement) =>
        this.uploadRecordingAnnouncement(announcement.index, announcement.announcement)
      )
    );

    return this.syncAnnouncements();
  }

  public async getVoicePreview(text: string, voice: VoiceOptions): Promise<Blob | null> {
    return firstValueFrom(this.post<Blob>('tts/preview', { text, voice }, { responseType: 'blob' }));
  }

  public saveSettings(settings: RecordingAnnouncementSettings): Promise<Response> {
    const payload: RecordingAnnouncementSettingsDto = { domain: { recordingAnnouncements: settings } };
    return firstValueFrom(
      this.appConfigService.updatePreferences(payload).pipe(
        tap(() => {
          this.announcementSettingsSubject.next(settings);
        })
      )
    );
  }

  public async filterVoicesByLanguage(id: string) {
    if (this.voicesByLanguage[id]) {
      this.filteredVoicesByLanguageSubject.next(this.voicesByLanguage[id]);
    } else {
      const voices = await this.getVoicesForLanguage(id);
      this.voicesByLanguage[id] = voices;
      this.filteredVoicesByLanguageSubject.next(voices);
    }
  }

  private async syncAnnouncements() {
    try {
      const list = await this.getList();
      if (list && list.length > 0) {
        const syncPromises = list.map(async (item) => {
          // get index from file name ([1-4].wav) so can get modification timestamp
          const groups = item.Key.match(/.*\/(?<index>\d).wav.*/)?.groups;
          if (groups && groups.index) {
            const index = groups.index;
            const dbAnnouncement = await skySwitchDB.announcements.get(index);
            if (item.LastModified !== dbAnnouncement?.lastModified) {
              await this.updateAnnouncement(index, item.LastModified);
            }
          }
        });
        await Promise.all(syncPromises);
      }
    } catch (error) {
      console.error('Error while syncing announcements:', error);
    }
  }

  private async uploadRecordingAnnouncement(index: number, annoucement: Blob): Promise<null> {
    const response = await firstValueFrom(
      this.post<{ preSignedUrl: string; publicUrl: string }>(`call-recording-announcements/${index + 1}`, {
        filename: `${index + 1}.wav`,
      })
    );

    return firstValueFrom(
      this.put<null>(response.preSignedUrl, annoucement, { headers: { 'Content-Type': 'audio/wav' } })
    );
  }

  private async updateAnnouncement(index: string, lastModified: string) {
    const announcement = await this.downloadAnnouncement(index);
    if (announcement) {
      const dbAnnouncement = await skySwitchDB.announcements.get(index);
      if (dbAnnouncement) {
        await skySwitchDB.announcements.update(index, { id: index, announcement, lastModified });
      } else {
        await skySwitchDB.announcements.put({ id: index, announcement, lastModified }, index);
      }
    }
  }

  private async checkAnnouncements() {
    await this.getPreferences();
    const settings = this.announcementSettingsSubject.value;

    if (
      settings.announcementType === 'tts' &&
      settings.ttsMessages.length === 4 &&
      settings.ttsLanguage.length > 0 &&
      settings.voice.length > 0
    ) {
      const voices = await this.getVoicesForLanguage(settings.ttsLanguage);
      const voice = voices.find((voiceOption) => voiceOption.id === settings.voice);
      if (voice) {
        const updated = await Promise.all(
          [0, 1, 2, 3].map(async (index): Promise<boolean> => {
            const annoucement = await this.getRecordingAnnouncement(index);
            if ((annoucement === undefined || annoucement.size === 0) && settings.ttsMessages[index].length > 0) {
              console.log(`Missing announcement recording for '${settings.ttsMessages[index]}', re-creating...`);
              try {
                const newAnnouncement = await this.getVoicePreview(settings.ttsMessages[index], voice);
                if (newAnnouncement) {
                  await this.uploadRecordingAnnouncement(index, newAnnouncement);
                  return true; // updated
                }
              } catch (error) {
                console.error(`Error while re-creating '${settings.ttsMessages[index]}' announcement:`, error);
              }
            }
            return false; // not updated
          })
        );

        if (updated.includes(true)) {
          await this.syncAnnouncements();
        }
      }
    }
  }

  private getList(): Promise<RecordingAnnouncementsListItem[]> {
    return firstValueFrom(this.get<RecordingAnnouncementsListItem[]>('call-recording-announcements'));
  }

  private downloadAnnouncement(index: string): Promise<Blob | null> {
    return firstValueFrom(
      this.post<Blob>(
        `call-recording-announcements/${index}/download`,
        { filename: `${index}.wav` },
        {
          responseType: 'blob',
        }
      )
    );
  }

  private async getPreferences(): Promise<void> {
    try {
      const preferences = await firstValueFrom(this.appConfigService.getPreferences());
      // We don't need to translate the response from preferences here because the
      // backend team checked and there were no translation keys uploaded.
      const recordingAnnouncements = preferences.domain?.recordingAnnouncements || {
        ...DefaultRecordingAnnouncementSettings,
        ttsMessages: DefaultRecordingAnnouncementSettings.ttsMessages.map((message) =>
          this.translateService.instant(message)
        ),
      };
      this.announcementSettingsSubject.next(recordingAnnouncements);
    } catch (error) {
      console.warn('Error while getting preferences in RecordingAnnouncementService:', error);
    }
  }

  private async getLanguages(): Promise<void> {
    try {
      const languages = await this.appConfigService.getTextToSpeechLanguages();
      this.languagesSubject.next(languages.languages);
    } catch (error) {
      console.error('Error while getting languages (RecordingAnnouncementService):', error);
    }
  }

  private async getVoicesForLanguage(id: string): Promise<VoiceOptions[]> {
    try {
      const voices = await this.appConfigService.getTextToSpeechVoices(id);

      return voices;
    } catch (error) {
      console.error('Error while getting voices for language (RecordingAnnouncementService):', error);
      return [];
    }
  }

  public async dropStoredAnnouncements(): Promise<void> {
    await skySwitchDB.resetAnnouncements();
  }
}
